import axios from "axios";
import { config } from "@/config";
import { clearAuthToken, loadAuthToken } from "@/utils/auth-token";

/* =====================================================================
   Typed client for the RTO backend (through the ms-taffarel gateway).
   Server-authoritative: the LP renders what these endpoints return —
   prices, collateral math, tenure and scheduling all come from here,
   never from tables hardcoded in the frontend.
   ===================================================================== */

export type Family = "COMFORT" | "CPX";
// NEW renders as "Nova" (≤ 5.000 km), USED as "Semi-nova" — LP vocabulary
// since the 03/09 base cut (RDT-468). The wire values kept their names.
export type BikeCondition = "NEW" | "USED";
// Which rule set the backend applied to this rider: PRE_CUT = activated up to
// 02/09 (CPX still valid), POST_CUT = activated from 03/09 (Comfort only, new
// collateral table). The tenure credit no longer depends on it (RDT-525).
// Display/tracking only.
export type ActivationRuleSet = "PRE_CUT" | "POST_CUT";
export type PricingModel = "TIER" | "LINEAR" | "UNLIMITED";
export type RtoPath = "KEEP" | "CHANGE";
export type CollateralPlan = "UPFRONT" | "INSTALLMENTS";

export type LpState =
  | "ineligible"
  | "offer"
  | "payment_pending"
  | "signature_pending"
  | "scheduling_pending"
  | "scheduled"
  | "cx_review"
  | "completed";

export interface RiderInfo {
  first_name: string | null;
  masked_email: string | null;
}

export interface StatusResponse {
  state: LpState;
  reason?: string | null;
  path?: RtoPath | null;
  // The bike the chosen plan is for — names the Comfort on the schedule and
  // booking screens after a resume (absent on an older backend → Nova).
  target_bike_condition?: BikeCondition | null;
  amount_due?: number | null;
  surplus?: number | null;
  payment_plan?: CollateralPlan | null;
  station_id?: number | null;
  pickup_date?: string | null;
  effective_term_months?: number | null;
  rider?: RiderInfo;
}

export interface PriceOption {
  pricing_model: PricingModel;
  price_id: number;
  weekly: number;
  variable_rate: number | null;
  ceiling: number | null;
}

export interface CollateralQuote {
  paid: number;
  // available=false (backend rule 2026-08-10): prior collateral already covers
  // the à-vista target — render ONLY the installment plan. Optional so an
  // older backend (field absent) keeps the pre-rule behavior of showing both.
  upfront: { target: number; due: number; available?: boolean };
  installments: {
    target_total: number;
    entry: number;
    entry_due: number;
    weekly_installments: number;
    installment_value: number;
    // LAST weekly installment; smaller than installment_value when the tail
    // is non-round ("15× R$50 + 1× R$25"). Absent on an older backend.
    last_installment_value?: number;
  };
  surplus: number;
}

export interface OfferPath {
  path: RtoPath;
  // Condition of the bike this plan is for (keep = own bike, change = the
  // Comfort delivered). Two CHANGE paths can coexist — Nova and Semi-nova —
  // for a Semi-nova rider who lost keep (RDT-468 AC5.2). Optional so an older
  // backend (field absent) keeps the pre-AC5.2 reading: change = Nova.
  bike_condition?: BikeCondition;
  options: PriceOption[];
  tenure_credit_months: number;
  effective_term_months: number;
  collateral: CollateralQuote;
  discloses_tier_to_linear: boolean;
  discloses_price_jump: boolean;
}

export interface Offer {
  segment: {
    family: Family;
    bike_condition: BikeCondition;
    pricing_model: PricingModel;
  };
  // Optional so an older backend (field absent) still renders.
  activation_rule_set?: ActivationRuleSet;
  tenure_months: number;
  paths: OfferPath[];
}

export interface EligibilityResponse {
  eligible: boolean;
  reason: string | null;
  rider: RiderInfo | null;
  offer: Offer | null;
}

export type PaymentMethod = "PIX" | "CHECKOUT";

export interface CollateralLinkResponse {
  amount_due: number;
  entry_due: number;
  surplus: number;
  init_point: string | null;
  pix: { qr_code: string; qr_code_base64: string } | null;
  schedule: {
    weekly_installments: number;
    installment_value: number;
    last_installment_value?: number;
  } | null;
  state: LpState;
}

export interface ConfirmResponse {
  success: true;
  alreadyMigrated?: boolean;
  effective_term_months?: number;
}

export interface Availability {
  window_start: string | null;
  window_end: string | null;
  // Every bookable day of the window, fully booked ones included. Which
  // weekdays those are is an ops decision living in the RTO_STATIONS flag
  // since 2026-09-08, so the LP renders this list instead of rebuilding it.
  // Optional so an older backend (field absent) still falls back to Tue–Sat.
  window_dates?: string[];
  stations: {
    station_id: number;
    name: string;
    address: string | null;
    days: { date: string; remaining: number }[];
  }[];
}

export interface ContractStatus {
  signed: boolean;
  signed_document_file_id?: number | null;
  signed_at?: string | null;
}

const rtoBase = (): string => `${config.apiUrl}/v1/rto-migration`;
const contractBase = (): string => `${config.apiUrl}/v1/rto-contract`;

const authHeaders = (): Record<string, string> => {
  const token = loadAuthToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

// Stable backend reason (e.g. "day_fully_booked") for a failed call, or null.
export const errorReason = (error: unknown): string | null => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: unknown } | undefined;
    if (typeof data?.message === "string") return data.message;
  }
  return null;
};

export const errorStatus = (error: unknown): number | null =>
  axios.isAxiosError(error) ? (error.response?.status ?? null) : null;

const handle401 = (error: unknown): never => {
  if (axios.isAxiosError(error) && error.response?.status === 401) {
    clearAuthToken();
  }
  throw error;
};

export const fetchStatus = async (): Promise<StatusResponse> => {
  try {
    const res = await axios.get<StatusResponse>(`${rtoBase()}/status`, {
      headers: authHeaders(),
    });
    return res.data;
  } catch (error) {
    return handle401(error);
  }
};

export const fetchEligibility = async (): Promise<EligibilityResponse> => {
  try {
    const res = await axios.get<EligibilityResponse>(
      `${rtoBase()}/eligibility`,
      {
        headers: authHeaders(),
      },
    );
    return res.data;
  } catch (error) {
    return handle401(error);
  }
};

export const postChoice = async (params: {
  path: RtoPath;
  pricingModel: PricingModel;
  // Echoed from the offer path so the backend freezes the Comfort the rider
  // picked (Nova vs Semi-nova on change). Omitted when the offer had none.
  bikeCondition?: BikeCondition;
}): Promise<void> => {
  try {
    await axios.post(
      `${rtoBase()}/choice`,
      {
        path: params.path,
        pricing_model: params.pricingModel,
        ...(params.bikeCondition
          ? { bike_condition: params.bikeCondition }
          : {}),
      },
      { headers: authHeaders() },
    );
  } catch (error) {
    return handle401(error);
  }
};

export const postCollateralLink = async (
  plan: CollateralPlan,
  method: PaymentMethod = "CHECKOUT",
  dueDate?: string,
): Promise<CollateralLinkResponse> => {
  try {
    const res = await axios.post<CollateralLinkResponse>(
      `${rtoBase()}/collateral/link`,
      { plan, method, due_date: dueDate },
      { headers: authHeaders() },
    );
    return res.data;
  } catch (error) {
    return handle401(error);
  }
};

export const fetchCollateralStatus = async (): Promise<{
  paid: boolean;
  state: LpState;
}> => {
  try {
    const res = await axios.get<{ paid: boolean; state: LpState }>(
      `${rtoBase()}/collateral/status`,
      { headers: authHeaders() },
    );
    return res.data;
  } catch (error) {
    return handle401(error);
  }
};

// Self-service cancel (Jerry 2026-08-03): voids the pending collateral charge
// server-side and parks the migration — the rider lands back on the offer.
// 409 = the record moved meanwhile (e.g. paid) — the caller resyncs via resume.
export const postCancelMigration = async (): Promise<{
  success: true;
  state: LpState;
}> => {
  try {
    const res = await axios.post<{ success: true; state: LpState }>(
      `${rtoBase()}/cancel`,
      {},
      { headers: authHeaders() },
    );
    return res.data;
  } catch (error) {
    return handle401(error);
  }
};

export const postConfirm = async (): Promise<ConfirmResponse> => {
  try {
    const res = await axios.post<ConfirmResponse>(
      `${rtoBase()}/confirm`,
      {},
      { headers: authHeaders() },
    );
    return res.data;
  } catch (error) {
    return handle401(error);
  }
};

export const fetchAvailability = async (): Promise<Availability> => {
  try {
    const res = await axios.get<Availability>(
      `${rtoBase()}/scheduling/availability`,
      {
        headers: authHeaders(),
      },
    );
    return res.data;
  } catch (error) {
    return handle401(error);
  }
};

export const postSchedule = async (params: {
  stationId: number;
  date: string;
}): Promise<{ success: true; station_id: number; date: string }> => {
  try {
    const res = await axios.post<{
      success: true;
      station_id: number;
      date: string;
    }>(
      `${rtoBase()}/schedule`,
      { station_id: params.stationId, date: params.date },
      { headers: authHeaders() },
    );
    return res.data;
  } catch (error) {
    return handle401(error);
  }
};

/* ------------------- contract (leoparda, rider-scoped) ------------------- */

export const fetchContractHtml = async (): Promise<string> => {
  try {
    const res = await axios.get<{ html: string }>(contractBase(), {
      headers: authHeaders(),
    });
    return res.data.html;
  } catch (error) {
    return handle401(error);
  }
};

export const fetchContractStatus = async (): Promise<ContractStatus> => {
  try {
    const res = await axios.get<ContractStatus>(`${contractBase()}/status`, {
      headers: authHeaders(),
    });
    return res.data;
  } catch (error) {
    return handle401(error);
  }
};

export const postContractSign = async (
  signatureDataUrl: string,
): Promise<{ success: boolean; already_signed: boolean }> => {
  try {
    const res = await axios.post<{ success: boolean; already_signed: boolean }>(
      `${contractBase()}/sign`,
      { signature: signatureDataUrl },
      { headers: authHeaders() },
    );
    return res.data;
  } catch (error) {
    return handle401(error);
  }
};
